import {
  ChangeDetectionStrategy,
  Component,
  Input,
  computed,
  inject,
  input,
  signal,
  type Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  Invoice,
  InvoiceClaimGenerator,
  InvoiceDisplayBloc,
  IPractitionerClaimSection,
} from '@principle-theorem/principle-core';
import {
  InvoiceStatus,
  TransactionProvider,
  type IAppointment,
  type IInvoice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import { ACCOUNT_CREDIT_TRANSACTION_PROVIDER } from '@principle-theorem/ng-principle-shared';
import {
  shareReplayCold,
  snapshot,
  wakeUpFunction,
  type WithRef,
} from '@principle-theorem/shared';
import {
  BehaviorSubject,
  ReplaySubject,
  combineLatest,
  of,
  type Observable,
} from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { TransactionProviderType } from '../transactions/transaction-providers/transaction-provider';
import { TransactionProviders } from '../transactions/transaction-providers/transaction-providers.service';
import {
  AppointmentCreditInputs,
  type AppointmentRefInput,
  type AutoApplyEnabledInput,
} from './appointment-credit-inputs';
import {
  AppointmentCreditStateTotals,
  type IAppointmentCreditStateResult,
} from './appointment-credit-state-totals';
import {
  AutoApplyDepositBloc,
  type AutoApplyDepositStatus,
} from './auto-apply-deposit.bloc';

@Component({
  selector: 'pr-invoice-transactions',
  templateUrl: './invoice-transactions.component.html',
  styleUrls: ['./invoice-transactions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InvoiceTransactionsComponent {
  private readonly _appointmentRef$ = new BehaviorSubject<AppointmentRefInput>(
    undefined
  );
  private readonly _autoApplyEnabled$ =
    new BehaviorSubject<AutoApplyEnabledInput>(false);
  private readonly _appointmentCreditStateTotals =
    new AppointmentCreditStateTotals();
  private readonly _autoApply: AutoApplyDepositBloc;
  private readonly _accountCredit = inject(ACCOUNT_CREDIT_TRANSACTION_PROVIDER);

  invoice$: ReplaySubject<WithRef<IInvoice>> = new ReplaySubject(1);
  transactions$: Observable<ITransaction[]>;
  bloc = new InvoiceDisplayBloc(this.invoice$);
  practitionerClaims$: Observable<IPractitionerClaimSection[]>;
  applyingCredit = signal(false);
  autoApplyStatus: Signal<AutoApplyDepositStatus>;
  autoApplyProgressLabel: Signal<string>;
  paymentActionsReady: Signal<boolean>;
  creditStateQueryFailed: Signal<boolean>;
  matchingCreditTotal: Signal<number>;
  unreservedCreditTotal: Signal<number>;
  availableCreditTotal: Signal<number>;
  accountCreditAvailable: Signal<boolean>;

  appointment = input<WithRef<IAppointment> | undefined>(undefined);

  @Input()
  set appointmentRef(appointmentRef: AppointmentRefInput) {
    this._appointmentRef$.next(appointmentRef);
  }

  @Input()
  set autoApplyAppointmentAccountCredits(
    autoApplyEnabled: AutoApplyEnabledInput
  ) {
    this._autoApplyEnabled$.next(autoApplyEnabled);
  }

  @Input()
  set invoice(invoice: WithRef<IInvoice>) {
    if (invoice) {
      this.invoice$.next(invoice);
    }
  }

  constructor(private _transactionProviders: TransactionProviders) {
    this._autoApply = new AutoApplyDepositBloc(
      this.applyingCredit,
      (invoice, appointmentRef) =>
        this._accountCredit.autoApplyAppointmentCredits(invoice, appointmentRef)
    );
    this.autoApplyStatus = this._autoApply.status;
    this.autoApplyProgressLabel = this._autoApply.progressLabel;
    this.paymentActionsReady = this._autoApply.paymentActionsReady;
    this.creditStateQueryFailed = this._autoApply.creditStateQueryFailed;

    this.transactions$ = this.invoice$.pipe(
      switchMap((invoice) => Invoice.transactions$(invoice)),
      switchMap((transactions) =>
        this._transactionProviders.filterTransactionsByProviderType(
          transactions,
          TransactionProviderType.Payment
        )
      )
    );
    this.practitionerClaims$ = this.invoice$.pipe(
      switchMap((invoice) =>
        InvoiceClaimGenerator.getPractitionerClaims(invoice)
      )
    );

    const appointmentCreditState$ = combineLatest([
      this.invoice$,
      this._appointmentRef$,
    ]).pipe(
      switchMap(([invoice, appointmentRef]) =>
        this._creditState$(invoice, appointmentRef)
      ),
      shareReplayCold()
    );

    const creditTotals = toSignal(
      appointmentCreditState$.pipe(
        map((creditStateResult) =>
          this._appointmentCreditStateTotals.get(creditStateResult)
        )
      ),
      {
        initialValue: {
          matchingTotal: 0,
          unreservedTotal: 0,
          availableTotal: 0,
        },
      }
    );

    this.matchingCreditTotal = computed(() => creditTotals().matchingTotal);
    this.unreservedCreditTotal = computed(() => creditTotals().unreservedTotal);
    this.availableCreditTotal = computed(() => creditTotals().availableTotal);

    const currentInvoice = toSignal(this.invoice$);
    this.accountCreditAvailable = computed(() => {
      const invoice = currentInvoice();
      const invoiceCanTakeCredit =
        !!invoice && invoice.status !== InvoiceStatus.Paid;
      return (
        this.availableCreditTotal() > 0 &&
        invoiceCanTakeCredit &&
        this.paymentActionsReady()
      );
    });

    combineLatest([
      this.invoice$,
      this._appointmentRef$,
      this._autoApplyEnabled$,
    ])
      .pipe(
        tap(([invoice, appointmentRef, autoApplyEnabled]) =>
          this._autoApply.syncGate({
            invoice,
            appointmentRef,
            autoApplyEnabled,
          })
        ),
        map(([invoice, appointmentRef, autoApplyEnabled]) => ({
          invoice,
          autoApplyTarget: AppointmentCreditInputs.resolveAutoApplyTarget(
            appointmentRef,
            autoApplyEnabled
          ),
        })),
        switchMap(({ invoice, autoApplyTarget }) =>
          autoApplyTarget
            ? appointmentCreditState$.pipe(
                map(({ invoicePath, creditState }) =>
                  creditState && invoicePath === invoice.ref.path
                    ? {
                        invoice,
                        appointmentRef: autoApplyTarget,
                        creditState,
                      }
                    : undefined
                )
              )
            : of(undefined)
        ),
        takeUntilDestroyed()
      )
      .subscribe((attempt) => {
        if (attempt) {
          void this._autoApply.attempt(attempt);
        }
      });

    void wakeUpFunction('http-accounting-addTransaction');
  }

  async useCredit(): Promise<void> {
    const option = await this._transactionProviders.findProviderById(
      TransactionProvider.AccountCredit
    );
    if (!option) {
      return;
    }
    this.applyingCredit.set(true);
    try {
      const invoice = await snapshot(this.invoice$);
      await this._transactionProviders.capture(option.provider, invoice);
    } finally {
      this.applyingCredit.set(false);
    }
  }

  /**
   * Reads one invoice's credit state, led by an "unknown yet" emission. Without
   * that lead-in a checkout that issues a second invoice keeps the first
   * invoice's totals on screen until the new read lands, offering credit
   * against the wrong invoice.
   */
  private _creditState$(
    invoice: WithRef<IInvoice>,
    appointmentRef: AppointmentRefInput
  ): Observable<IAppointmentCreditStateResult> {
    const creditStateUnknown: IAppointmentCreditStateResult = {
      invoicePath: invoice.ref.path,
      creditState: undefined,
      creditStateQueryFailed: false,
    };
    if (!AppointmentCreditInputs.isResolvedAppointmentRef(appointmentRef)) {
      return of(creditStateUnknown);
    }
    return this._accountCredit
      .getAppointmentCreditState$(invoice, appointmentRef)
      .pipe(
        map((creditState) => ({
          invoicePath: invoice.ref.path,
          creditState,
          creditStateQueryFailed: false,
        })),
        catchError(() => {
          this._autoApply.markCreditStateFailed(invoice);
          return of({
            invoicePath: invoice.ref.path,
            creditState: undefined,
            creditStateQueryFailed: true,
          });
        }),
        startWith(creditStateUnknown)
      );
  }
}
